
import { Component, Vue, Prop, Watch, Emit, Ref } from 'vue-property-decorator'
import type { DairyOption, HomeworkOption } from '@xt/client/entities/course-v2/user-content'
import type { CityTypeInfo, ConfirmModalOptions, Moment, ModalDataType } from '@xt/client/entities/kindred-spirit/kindred-spirit'
import { EnumCityStatus } from '@xt/client/entities/kindred-spirit/guild'
import IntroPanel from '../../components/IntroPanel/index.vue'
import MomentCard from '../../components/MomentCard/index.vue'
import UserContentPop from '@xt/www/components/classhour/pop-content/index.vue'
import VirtualWaterfall from '@xt/www/components/virtualWaterFall/index.vue'
import InfiniteLoading from '@xt/www/components/infinite-loadingv2/index.vue'
import { Observer } from 'mobx-vue'
import { Bind, Debounce } from 'lodash-decorators'
import ConfirmModal from '../../components/ConfirmModal/index.vue'

enum EnumStatus {
  Assistance = 'assistance',
  Share = 'share',
  Recruit = 'recruit'
}

@Observer
@Component({
  components: {
    UserContentPop,
    IntroPanel,
    MomentCard,
    VirtualWaterfall,
    InfiniteLoading,
    ConfirmModal
  },
  async asyncData(ctx) {
    const cityId = ctx.params.id
    const data = await ctx.store.$tonghao.Guild.initCityInfoById(cityId)
    await ctx.store.$tonghao.Guild.getHasJoinedCity()
    const joinedCity = ctx.store.$tonghao.Guild.joinedCity
    const isJoinCity = joinedCity ? joinedCity.id.toString() === cityId : false
    const isManager = joinedCity ? joinedCity['managerId'] === ctx.store.$storeUser.UserInfo.id : false

    return {
      cityId,
      currentCityInfo: data,
      hasJoin: !!joinedCity,
      isJoinCity,
      isManager
    }
  },
  validate({ params }) {
    return /^\d+$/.test(params.id)
  }
})
export default class CityPage extends Vue {
  currentCityInfo: CityTypeInfo
  needLogin: boolean = false
  cityId: string
  hasJoin: boolean // 是否加入分桐
  isJoinCity: boolean // 当前分桐是否是已加入的分桐
  isManager: boolean // 是否为当前分桐管理员

  get EnumStatus() {
    return EnumStatus
  }

  get hideWaterfall() {
    if (this.currentCityInfo.status === EnumCityStatus.Inited) return false

    if (this.currentCityInfo.recruitStatus === true) return EnumStatus.Recruit

    if (this.currentCityInfo.status === EnumCityStatus.Assistance) {
      return !this.hasJoin ? EnumStatus.Assistance : EnumStatus.Share
    }

    return false
  }
  get contentData() {
    // 未命中指定状态 ｜ 分桐已创建
    if (!this.hideWaterfall) return null

    const operations: Array<{
      key: EnumStatus
      imgPath: string
      desc: string
      btnText: string
    }> = [
      {
        key: EnumStatus.Assistance,
        imgPath: 'assistance',
        desc: '加入城市分桐，助力分桐开放。',
        btnText: '助力'
      },
      {
        key: EnumStatus.Share,
        imgPath: 'assistance',
        desc: '邀请桐好加入，助力分桐开放。',
        btnText: '分享'
      },
      {
        key: EnumStatus.Recruit,
        imgPath: 'recruit',
        desc: '成为城市分桐负责人，助力分桐开放',
        btnText: '负责人招募'
      }
    ]
    const index = operations.findIndex(item => item.key === this.hideWaterfall)

    return {
      ...operations[index],
      img: `${this.$store.$global.static}/images/topic/pc/city-info-status-${operations[index].imgPath}.png`
    }
  }

  @Ref()
  userContentPop: {
    showPop: (options: HomeworkOption | DairyOption) => void
    hidePop: () => void
  }

  @Ref()
  confirmModal: {
    show: (options: ConfirmModalOptions) => Promise<boolean>
  }

  get EnumCityStatus() {
    return EnumCityStatus
  }

  get UserInfo() {
    return this.$store.$storeUser.UserInfo
  }

  get GuildStore() {
    return this.$store.$tonghao.Guild
  }

  get TonghaoStore() {
    return this.$store.$tonghao
  }

  get pagePagination() {
    return this.TonghaoStore.pagePagination
  }

  get UserStore() {
    return this.$store.$storeUser
  }

  compactImageSize(originWidth: number, originHeight: number) {
    const limitWidth = 194
    const shouldHeigth = Math.floor((limitWidth * originHeight) / originWidth)

    return [limitWidth, shouldHeigth]
  }

  getImageHeight(originWidth: number, originHeight: number) {
    return Math.max(100, Math.min(248, this.compactImageSize(originWidth, originHeight)[1]))
  }

  getTextWidth(text, font = '14px sans-serif') {
    if (!text) return null
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    ctx.font = font
    return ctx.measureText(text).width
  }

  // 获取动态高度
  getComptedHeightList(list) {
    return this.toJS(list).map(item => {
      let totalHeight: number = 0

      // 计算图片高度
      totalHeight +=
        Array.isArray(this.toJS(item.pictures)) && item.pictures.length > 0
          ? this.getImageHeight(item.pictures[0].originWidth, item.pictures[0].originHeight)
          : 248
      // 计算 title 间距
      totalHeight += item.sourceDescription ? 8 : 10
      // 计算 title 高度，如果超过200（一行文字宽度），则高度为36，否则为18
      totalHeight += (this.getTextWidth(item.title || item.content) ?? 0) > 200 ? 36 : 18
      // 计算 card-info 间距 + 高度 + 底部间距
      totalHeight += 12 + 28 + 12

      return {
        ...item,
        height: totalHeight
      }
    })
  }

  handleClickMoment(momentId: string) {
    if (!momentId) return

    this.userContentPop?.showPop({
      type: 'dairy',
      momentId
    })
  }

  // 卡片点赞事件
  @Debounce(350, { leading: true, trailing: false })
  @Bind()
  async handleLikeMoment(item: Moment) {
    // 未登录防御
    if (!this.UserInfo.id) {
      this.$store.$storeUser.onToggleVisible(true)
      return
    }

    try {
      await this.TonghaoStore.onLikeMoment(item)
      this.$message.success('点赞成功')
    } catch (e) {
      if (e instanceof Error) {
        this.$message.error(e.message)
      }
    }
  }

  handleClosed() {
    if (this.needLogin) {
      this.$store.$storeUser.onToggleVisible(true)
      this.needLogin = false
    }
  }

  handleOpenLogin() {
    this.needLogin = true
    this.userContentPop.hidePop()
  }

  // 弹窗内部删除事件
  handleRemoveSuccess(data: { type: 'dairy'; momentId: string }) {
    if (data.type === 'dairy' && data.momentId) {
      // 删除缓存的数据
      this.TonghaoStore.removeItem(data.momentId)
    }
  }

  // 弹窗内部收藏事件
  handleLikeSuccess(data: { momentId: string }) {
    if (data.momentId) {
      this.TonghaoStore.likeItem(data.momentId)
    }
  }

  openImproveModal() {
    this.confirmModal.show({
      title: `完善个人信息`,
      description: `・填写手机号，方便分桐负责人联络\n・简单介绍自己，聊聊你的兴趣爱好吧~`,
      layoutClassName: 'improve-confirm-modal',
      confirmedHide: true,
      confirmText: '去完善',
      cancelText: '取消',
      onOk: () => {
        this.$msg('敬请期待~')
      }
    })
  }
  onJoinSuccess() {
    this.$msg('加入成功~', 'success')

    setTimeout(() => {
      location.reload()
    }, 200)
  }
  async openAssistantModal(data: ModalDataType) {
    const res = await this.confirmModal.show({
      title: `为${data.cityName}分桐助力`,
      description: '助力后将自动加入该分桐，并增加分桐热力值。达到目标后可推进分桐开放。',
      layoutClassName: 'assistant-confirm-modal',
      confirmedHide: true,
      confirmText: '立即助力',
      cancelText: '稍后再说',
      backgroundUrl: this.$store.$global.static + '/images/topic/pc/assistant-pop-bg.png'
    })
    if (res === true) {
      const res = await this.GuildStore.joinGuild(data.id)
      if (res === true) {
        this.onJoinSuccess()
      }
    }
  }
  async openRecruitModal(data: ModalDataType) {
    const res = await this.confirmModal.show({
      title: `成为${data.cityName}分桐负责人`,
      description: `报名参与负责人招募，即视为您已自动加入该分桐。待负责人招募工作全部完成后，${data.cityName}分桐将正式开放。`,
      layoutClassName: 'recruit-confirm-modal',
      confirmedHide: true,
      confirmText: '确认',
      cancelText: '再想想',
      backgroundUrl: this.$store.$global.static + '/images/topic/pc/recruit-pop-bg.png'
    })

    if (res === true) {
      const res = await this.GuildStore.joinGuild(data.id)
      if (res === true) {
        this.onJoinSuccess()
      }
    }
  }
  async openJoinModal(data: ModalDataType) {
    const res = await this.confirmModal.show({
      title: `${data.cityName}分桐`,
      description: '桐学仅可选择一座常住城市加入，与桐好笔墨相伴，雅会常聚。',
      layoutClassName: 'join-confirm-modal',
      confirmedHide: true,
      confirmText: '确认加入',
      cancelText: '再想想',
      backgroundUrl: this.$store.$global.static + '/images/topic/pc/assistant-pop-bg.png'
    })

    if (res === true) {
      const res = await this.GuildStore.joinGuild(data.id)
      if (res === true) {
        this.onJoinSuccess()
      }
    }
  }

  // 加入操作
  handleOperation(key: EnumStatus) {
    const modalData: ModalDataType = {
      id: this.currentCityInfo.id,
      cityName: this.currentCityInfo.cityName,
      adcode: this.currentCityInfo.adcode
    }

    if (key === EnumStatus.Assistance) {
      this.openAssistantModal(modalData)
    } else if (key === EnumStatus.Share) {
    } else if (this.currentCityInfo.recruitStatus === true && key === EnumStatus.Recruit) {
      this.openRecruitModal(modalData)
    }
  }

  created(): void {}

  mounted(): void {}

  updated(): void {}

  destroyed(): void {}
}
